/**
 * What: Subscribes to an s-banner's `dismiss` DOM event in a React-18-safe way.
 * Transport / data-flow: Callback in, real DOM listener out with effect cleanup; the listener is
 *   re-attached when `onDismiss` changes, so no ref is written during render.
 *
 * React 18 silently drops function-valued `on*` props on custom elements (it only
 * wires its own known events), so `onDismiss={fn}` JSX on an `s-banner` never fires —
 * in tests or production. Attach the `dismiss` event directly instead.
 */
import { useEffect, useRef } from 'react'

type BannerElement = HTMLElementTagNameMap['s-banner']

/** Returns a ref to spread onto the `s-banner`; `onDismiss` runs on close-press while active. */
export function useBannerDismiss(active: boolean, onDismiss: () => void) {
  const ref = useRef<BannerElement | null>(null)
  useEffect(() => {
    if (!active) return
    const node = ref.current
    if (!node) return
    const listener = () => onDismiss()
    node.addEventListener('dismiss', listener)
    return () => node.removeEventListener('dismiss', listener)
  }, [active, onDismiss])
  return ref
}
